import { BUILD_HASH } from "@/config/version";

export interface PerformanceMetrics {
  fcp: number | null; // First Contentful Paint (ms)
  lcp: number | null; // Largest Contentful Paint (ms)
  memoryMB: number | null; // Memory usage (MB)
  domSize: number; // Number of DOM nodes
  jsBundleKB: number | null; // Estimated JS bundle size (KB)
  timestamp: number;
  buildHash: string;
  url: string;
}

const STORAGE_KEY = 'wordpop-performance-metrics';
const MAX_EVENTS = 50;

// Capture performance metrics
export function capturePerformanceMetrics(): PerformanceMetrics {
  const perfEntries = performance.getEntriesByType('paint');
  const fcp = perfEntries.find(e => e.name === 'first-contentful-paint')?.startTime || null;
  
  let lcp: number | null = null;
  try {
    const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
    if (lcpEntries.length > 0) {
      lcp = (lcpEntries[lcpEntries.length - 1] as any).startTime;
    }
  } catch (e) {
    // LCP not supported
  }

  let memoryMB: number | null = null;
  try {
    const memory = (performance as any).memory;
    if (memory) {
      memoryMB = Math.round(memory.usedJSHeapSize / 1024 / 1024 * 10) / 10;
    }
  } catch (e) {
    // Memory API not available
  }

  const domSize = document.querySelectorAll('*').length;

  let jsBundleKB: number | null = null;
  try {
    const jsResources = performance.getEntriesByType('resource')
      .filter(r => r.name.endsWith('.js'));
    const totalSize = jsResources.reduce((sum, r: any) => sum + (r.transferSize || 0), 0);
    jsBundleKB = Math.round(totalSize / 1024);
  } catch (e) {
    // Resource timing not available
  }

  return {
    fcp: fcp ? Math.round(fcp) : null,
    lcp: lcp ? Math.round(lcp) : null,
    memoryMB,
    domSize,
    jsBundleKB,
    timestamp: Date.now(),
    buildHash: BUILD_HASH,
    url: window.location.pathname,
  };
}

// Track performance metrics
export function trackPerformanceMetrics(): void {
  try {
    const metrics = capturePerformanceMetrics();
    const existingMetrics = getPerformanceMetrics();
    const updatedMetrics = [metrics, ...existingMetrics].slice(0, MAX_EVENTS);
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedMetrics));
    
    console.log('[Performance Analytics] Captured:', {
      FCP: metrics.fcp ? `${metrics.fcp}ms` : 'N/A',
      LCP: metrics.lcp ? `${metrics.lcp}ms` : 'N/A',
      Memory: metrics.memoryMB ? `${metrics.memoryMB}MB` : 'N/A',
      DOM: metrics.domSize,
      JS: metrics.jsBundleKB ? `${metrics.jsBundleKB}KB` : 'N/A',
    });
  } catch (error) {
    console.error('Failed to track performance metrics:', error);
  }
}

// Get all performance metrics
export function getPerformanceMetrics(): PerformanceMetrics[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    return JSON.parse(data);
  } catch (error) {
    console.error('Failed to retrieve performance metrics:', error);
    return [];
  }
}

// Get aggregated performance analytics
export interface PerformanceAnalyticsSummary {
  totalCaptures: number;
  averageFCP: number;
  averageLCP: number;
  averageMemoryMB: number;
  averageDOMSize: number;
  averageJSBundleKB: number;
  minFCP: number;
  maxFCP: number;
  minLCP: number;
  maxLCP: number;
  buildBreakdown: Record<string, number>;
  recentMetrics: PerformanceMetrics[];
}

export function getPerformanceAnalytics(): PerformanceAnalyticsSummary {
  const metrics = getPerformanceMetrics();
  
  const fcpValues = metrics.filter(m => m.fcp !== null).map(m => m.fcp!);
  const lcpValues = metrics.filter(m => m.lcp !== null).map(m => m.lcp!);
  const memoryValues = metrics.filter(m => m.memoryMB !== null).map(m => m.memoryMB!);
  const domValues = metrics.map(m => m.domSize);
  const jsValues = metrics.filter(m => m.jsBundleKB !== null).map(m => m.jsBundleKB!);
  
  const avg = (arr: number[]) => arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0;
  const min = (arr: number[]) => arr.length > 0 ? Math.min(...arr) : 0;
  const max = (arr: number[]) => arr.length > 0 ? Math.max(...arr) : 0;
  
  // Build breakdown
  const buildBreakdown: Record<string, number> = {};
  metrics.forEach(metric => {
    buildBreakdown[metric.buildHash] = (buildBreakdown[metric.buildHash] || 0) + 1;
  });
  
  return {
    totalCaptures: metrics.length,
    averageFCP: avg(fcpValues),
    averageLCP: avg(lcpValues),
    averageMemoryMB: Math.round(avg(memoryValues) * 10) / 10,
    averageDOMSize: avg(domValues),
    averageJSBundleKB: avg(jsValues),
    minFCP: min(fcpValues),
    maxFCP: max(fcpValues),
    minLCP: min(lcpValues),
    maxLCP: max(lcpValues),
    buildBreakdown,
    recentMetrics: metrics.slice(0, 10),
  };
}

// Clear performance analytics
export function clearPerformanceAnalytics(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[Performance Analytics] Analytics cleared');
  } catch (error) {
    console.error('Failed to clear performance analytics:', error);
  }
}
